'use client';
import * as React from 'react';
import { Icon } from '@/components/site/ui';
import { cn } from '@/lib/utils';

/**
 * Horizontal numbered step rail: a circle per step joined by connectors, the concluded ones
 * check-marked. For a flow whose steps are decisions the user walks through (a modal wizard,
 * an onboarding card) — not for progress of a task, which is `ProgressBar`.
 *
 * Labels arrive translated. The list is NOT `aria-hidden`: the active step carries
 * `aria-current="step"` and only the glyphs and connectors are decorative, so a screen reader
 * announces where the user is instead of nothing at all.
 */
export function Stepper({
  steps,
  current,
  ariaLabel,
  srPosition,
  tone = 'default',
  className,
}: {
  /** Translated labels, in order. `done` overrides the position rule for a checklist whose
   *  steps can be satisfied out of order (attach a document, confirm details). */
  steps: { id: string; label: string; done?: boolean }[];
  /** 1-based index of the active step. Anything below it renders as concluded. */
  current: number;
  /** Translated name of the rail itself. */
  ariaLabel: string;
  /** Translated "Passo 2 de 3", announced once before the list. */
  srPosition: string;
  /** 'onDark' for a rail sitting on a dark panel header. */
  tone?: 'default' | 'onDark';
  className?: string;
}) {
  const dark = tone === 'onDark';
  // Past five steps the uppercase labels stop fitting the panel and the last one gets
  // clipped mid-word (a six-step flow cut "REVISÃO" in half). From there only the ACTIVE
  // step keeps its visible label — the others are numbered circles, and their names stay in
  // the accessibility tree, so nothing is lost to a screen reader.
  const compact = steps.length > 5;
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <span className="sr-only">{srPosition}</span>
      <ol className="flex flex-1 items-center gap-2" aria-label={ariaLabel}>
        {steps.map((s, i) => {
          const n = i + 1;
          const done = s.done ?? n < current;
          const active = !done && n === current;
          return (
            <React.Fragment key={s.id}>
              <li
                className="flex shrink-0 items-center gap-1.5"
                aria-current={active ? 'step' : undefined}
              >
                <span
                  aria-hidden
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded-full text-[0.6875rem] font-bold',
                    done
                      ? dark
                        ? 'bg-highlight-soft text-highlight-foreground'
                        : 'bg-medlux text-white'
                      : active
                        ? dark
                          ? 'bg-white text-medlux-strong'
                          : 'bg-medlux text-white'
                        : dark
                          ? 'bg-white/15 text-white/70'
                          : 'bg-muted text-muted-foreground',
                  )}
                >
                  {done ? <Icon name="tick-02" size={13} /> : n}
                </span>
                <span
                  className={cn(
                    'text-[0.6875rem] font-semibold uppercase tracking-[0.06em]',
                    compact && !active && 'sr-only',
                    // On a phone only the active label fits beside the circles; the rest stay readable to screen readers.
                    !active && 'max-sm:sr-only',
                    active
                      ? dark
                        ? 'text-white'
                        : 'text-foreground'
                      : dark
                        ? 'text-white/60'
                        : 'text-muted-foreground',
                  )}
                >
                  {s.label}
                </span>
              </li>
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={cn(
                    'h-px min-w-4 flex-1',
                    done ? (dark ? 'bg-highlight-soft' : 'bg-medlux') : dark ? 'bg-white/20' : 'bg-border',
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}
